import { useState } from 'react';
import {
  Box,
  Stack,
  Alert,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { useSiteDetails } from '../../../hooks/useSiteDetails';
import { useAddPlotForm } from '../../../hooks/useAddPlotForm';
import { ConfirmAddPlotDialog } from '../../molecules/ConfirmAddPlotDialog';
import type { AddPlotFormData } from '../../../validation/addPlotSchema';

interface AddPlotPanelProps {
  siteId: number;
}

/**
 * Panel for adding plots to a site
 * Organism component following Atomic Design Structure
 * Follows the form architecture pattern with 3-layer structure
 */
export function AddPlotPanel({ siteId }: AddPlotPanelProps) {
  const { closePanel } = useSitePanelState();
  const { data: site } = useSiteDetails(siteId);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<AddPlotFormData | null>(null);

  const { form, submitData, isSubmitting, successMessage, errorMessage } =
    useAddPlotForm(siteId, {
      onSuccess: () => {
        // Close panel after successful submission
        closePanel();
      },
    });

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = form;

  // Handle form submission - show confirmation dialog
  const onSubmit = async (data: AddPlotFormData) => {
    setPendingData(data);
    setShowConfirm(true);
  };

  // Handle confirmation - actually submit the form
  const handleConfirm = async () => {
    setShowConfirm(false);
    if (pendingData) {
      await submitData(pendingData);
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingData(null);
  };

  // No site data (initial loading or truly no data)
  if (!site) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Success/Error messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Info banner */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Create a new empty plot for this site. You can add labour items to it
        after creation.
      </Alert>

      {/* Form */}
      <FormProvider {...form}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          <Stack spacing={3} sx={{ flex: 1 }}>
            {/* Plot Name */}
            <TextField
              label="Plot Name *"
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message?.toString()}
              fullWidth
              autoFocus
              placeholder="e.g., Plot A1"
            />

            {/* Plot Number */}
            <TextField
              label="Plot Number *"
              {...register('plotNumber')}
              error={!!errors.plotNumber}
              helperText={errors.plotNumber?.message?.toString()}
              fullWidth
              placeholder="e.g., A1-001"
            />
          </Stack>

          {/* Site Information */}
          <Box
            sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Adding to site: <strong>{site.name}</strong>
            </Typography>
          </Box>

          {/* Actions */}
          <Box display="flex" gap={2} mt={3}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              fullWidth
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Create Plot'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => closePanel()}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </Box>
        </Box>
      </FormProvider>

      {/* Confirmation Dialog */}
      <ConfirmAddPlotDialog
        open={showConfirm}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        plotName={pendingData?.name}
        plotNumber={pendingData?.plotNumber}
        siteName={site?.name}
      />
    </Box>
  );
}
